import React, { useState, useEffect, useContext, Fragment } from 'react';
import styled from 'styled-components';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { graphql, Link } from 'gatsby';
import { MdSearch } from '@react-icons/all-files/md/MdSearch';
import { MdArrowForward } from '@react-icons/all-files/md/MdArrowForward';
import { MdArrowBack } from '@react-icons/all-files/md/MdArrowBack';
import {
  Text,
  Box,
  Flex,
  Layout,
  Wrapper,
  GatsbyImage,
  BlogCard,
  BlogLinks,
  StyledTextField,
} from '../components';
import { GlobalContext } from '../context';
import { getModifiedPath, mapMenu } from '../util/helper';
import { languages } from '../util/data';
import { theme } from '../style';

const Headline = styled(Flex)`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  box-shadow: rgb(0 0 0 / 10%) 0px 8px 16px, rgb(0 0 0 / 8%) 0px 4px 8px;

  @media ${theme.devices.md} {
    position: relative;
    transform: translateY(0);
    margin-top: 32px;
  }
`;

const StyledButton = styled(Button)`
  background-color: ${theme.colors.grey.v7} !important;
  text-transform: none !important;
  font-family: ${theme.fonts.primary} !important;

  :hover {
    background-color: black !important;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${({ color }) => color || theme.colors.grey.v5};
`;

const BlogList = ({ pageContext, data }) => {
  const [value, setValue] = useState('');
  const { setLanguage, setMenu, setTranslations } =
    useContext(GlobalContext) || {};

  const { wpMenu, allWpPost } = data || {};
  const { title, link, excerpt, featuredImage } = allWpPost?.nodes[0] || {};
  const { previousPagePath, nextPagePath, lang } = pageContext;

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  useEffect(() => {
    const language = languages[lang] || lang;
    setLanguage(language);
    setMenu(mapMenu(wpMenu));
    setTranslations([]);
  }, [wpMenu, lang, setMenu, setLanguage, setTranslations]);

  return (
    <Layout>
      <Wrapper pt={6}>
        <Box position="relative" mt={6}>
          {featuredImage && (
            <Box width={[1, 1, 1, 2 / 3]}>
              <GatsbyImage
                src={featuredImage.node.sourceUrl}
                alt={featuredImage.node.altText}
                borderradius="10px"
                maxheight="500px"
                width="100%"
                height="auto"
              />
            </Box>
          )}
          <Headline
            width={[1, 1, 1, 1 / 2]}
            flexDirection="column"
            backgroundColor="white"
            borderRadius="10px"
            p={4}
          >
            <StyledLink color="black" to={getModifiedPath(link)}>
              <Text as="h3" fontWeight="bold" mb={3}>
                {title}
              </Text>
            </StyledLink>
            <BlogLinks post={allWpPost?.nodes?.[0]} />
            <Text
              color="grey.v5"
              dangerouslySetInnerHTML={{
                __html: excerpt.replace('[&hellip;]', '&hellip;'),
              }}
            />
          </Headline>
        </Box>

        <Flex flexWrap="wrap" my={4}>
          <Box width={[1, 1, 2 / 3]} mb={[3, 3, 0]}>
            {Array.isArray(data.allWpCategory.nodes) &&
              data.allWpCategory.nodes.map((category, index) => (
                <StyledButton
                  variant="contained"
                  href={getModifiedPath(category.link)}
                  key={index}
                  sx={{ margin: '5px 10px 5px 0' }}
                >
                  {category.name}
                </StyledButton>
              ))}
          </Box>
          <Box width={[1, 1, 1 / 3]}>
            <StyledTextField
              label="Search"
              type="text"
              value={value}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <IconButton>
                    <MdSearch />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Flex>

        <Grid container spacing={4} sx={{ mb: 8 }}>
          {allWpPost?.nodes?.map((post, index) => (
            <Fragment key={index}>
              {index !== 0 && (
                <Grid item xs={12} sm={6} md={4}>
                  <BlogCard post={post} />
                </Grid>
              )}
            </Fragment>
          ))}
        </Grid>

        <Flex flexWrap="wrap" justifyContent="space-between" mb={5}>
          <Box>
            {nextPagePath && (
              <StyledButton
                variant="contained"
                startIcon={<MdArrowBack />}
                href={nextPagePath}
                rel="next"
              >
                Older Entries
              </StyledButton>
            )}
          </Box>
          <Box>
            {previousPagePath && (
              <StyledButton
                variant="contained"
                endIcon={<MdArrowForward />}
                href={previousPagePath}
                rel="prev"
              >
                Recent Entries
              </StyledButton>
            )}
          </Box>
        </Flex>
      </Wrapper>
    </Layout>
  );
};

export const query = graphql`
  query ($limit: Int!, $skip: Int!, $lang: String!, $menu: String!) {
    wpMenu(slug: { eq: $menu }) {
      menuItems {
        nodes {
          parentId
          label
          path
          childItems {
            nodes {
              parentId
              label
              path
              childItems {
                nodes {
                  parentId
                  label
                  path
                }
              }
            }
          }
        }
      }
    }
    allWpCategory(
      limit: 5
      filter: {
        language: { slug: { eq: $lang } }
        slug: { regex: "/^((?!uncategorized).)*$/" }
      }
    ) {
      nodes {
        name
        link
      }
    }
    allWpPost(
      limit: $limit
      skip: $skip
      sort: { fields: date, order: DESC }
      filter: { language: { slug: { eq: $lang } } }
    ) {
      nodes {
        title
        link
        date(formatString: "MMMM DD, YYYY")
        author {
          node {
            name
            username
            url
            uri
            slug
            locale
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
        excerpt
        content
        categories {
          nodes {
            name
            slug
            link
            uri
          }
        }
      }
    }
  }
`;

export default BlogList;
